import { useShowAlert } from '~/composables/use-show-alert'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type Player from 'video.js/dist/types/player'
import { useState } from '#app'
type VideoJsFactory = typeof import('video.js').default

const MAX_VIDEO_WIDTH_PX = 1100
const VIDEO_VIEWPORT_PADDING_RATIO = 0.95
const FALLBACK_VIDEO_ASPECT_RATIO = 16 / 9

function inferVideoMimeType(url: string): string | undefined {
    const normalizedUrl = url.split('?')[0].toLowerCase()

    if (normalizedUrl.endsWith('.m3u8')) {
        return 'application/x-mpegURL'
    }

    if (normalizedUrl.endsWith('.mpd')) {
        return 'application/dash+xml'
    }

    if (normalizedUrl.endsWith('.webm')) {
        return 'video/webm'
    }

    if (normalizedUrl.endsWith('.ogg') || normalizedUrl.endsWith('.ogv')) {
        return 'video/ogg'
    }

    if (normalizedUrl.endsWith('.mp4') || normalizedUrl.endsWith('.m4v')) {
        return 'video/mp4'
    }

    return undefined
}

export function useAgreementVideo(
    videoUrl: string | null,
    allowSkippingVideo: boolean,
    validateContinue: () => boolean,
    handleContinue: () => void
) {
    // This is so if they cancel out of the login modal, they do not have to watch the video again
    let alreadyWatchedVideo = false
    let hasHandledContinue = false

    const isWatchingVideo = useState('isWatchingWistiaVideo', () => false)
    isWatchingVideo.value = false
    const isLoadingVideo = ref(false)

    let videoPlayer: Player | undefined = undefined
    let videoJsFactory: VideoJsFactory | undefined = undefined
    let videoJsLoadPromise: Promise<VideoJsFactory> | undefined = undefined
    let playerReadyPromise: Promise<void> | undefined = undefined
    let watchThresholdSeconds = 5
    let playerContainerElement: HTMLDivElement | undefined = undefined
    let playbackStallTimeoutId: ReturnType<typeof setTimeout> | undefined =
        undefined

    const showAlert = useShowAlert()

    function ensureVideoJsLoaded(): Promise<VideoJsFactory> {
        if (videoJsFactory !== undefined) {
            return Promise.resolve(videoJsFactory)
        }

        if (videoJsLoadPromise !== undefined) {
            return videoJsLoadPromise
        }

        videoJsLoadPromise = Promise.all([
            import('video.js'),
            import('video.js/dist/video-js.css')
        ]).then(([videoJsModule]) => {
            videoJsFactory = videoJsModule.default
            return videoJsFactory
        })

        return videoJsLoadPromise
    }

    function clearPlaybackStallTimeout() {
        if (playbackStallTimeoutId !== undefined) {
            clearTimeout(playbackStallTimeoutId)
            playbackStallTimeoutId = undefined
        }
    }

    function getVideoAspectRatio(player: Player): number {
        const videoWidth = player.videoWidth()
        const videoHeight = player.videoHeight()

        if (
            typeof videoWidth === 'number' &&
            typeof videoHeight === 'number' &&
            videoWidth > 0 &&
            videoHeight > 0
        ) {
            return videoWidth / videoHeight
        }

        return FALLBACK_VIDEO_ASPECT_RATIO
    }

    function updatePlayerDimensions(player: Player) {
        if (typeof window === 'undefined') {
            return
        }

        const maxWidth = Math.min(
            window.innerWidth * VIDEO_VIEWPORT_PADDING_RATIO,
            MAX_VIDEO_WIDTH_PX
        )
        const maxHeight = window.innerHeight * VIDEO_VIEWPORT_PADDING_RATIO
        const aspectRatio = getVideoAspectRatio(player)

        let width = maxWidth
        let height = width / aspectRatio

        if (height > maxHeight) {
            height = maxHeight
            width = height * aspectRatio
        }

        player.width(width)
        player.height(height)
    }

    function handleWindowResize() {
        if (videoPlayer === undefined) {
            return
        }

        updatePlayerDimensions(videoPlayer)
    }

    function schedulePlaybackStallTimeout() {
        clearPlaybackStallTimeout()

        playbackStallTimeoutId = setTimeout(() => {
            if (!isWatchingVideo.value || alreadyWatchedVideo) {
                return
            }

            hideVideo()
            resetPlayer()
            showAlert(
                'Heads Up!',
                'The video appears to be stalled due to a connection issue. Please try again.'
            )
        }, 20000)
    }

    function handleWatchedAndContinue() {
        if (alreadyWatchedVideo && !hasHandledContinue) {
            hasHandledContinue = true
            handleContinue()
        }
    }

    function hideVideo() {
        if (playerContainerElement !== undefined) {
            playerContainerElement.style.display = 'none'
        }

        if (videoPlayer !== undefined) {
            videoPlayer.pause()
        }

        clearPlaybackStallTimeout()
        isWatchingVideo.value = false

        if (typeof document !== 'undefined') {
            document.body.classList.remove('overflow-hidden')
        }

        handleWatchedAndContinue()
    }

    function resetPlayer() {
        clearPlaybackStallTimeout()

        if (videoPlayer !== undefined) {
            videoPlayer.dispose()
            videoPlayer = undefined
        }
        playerReadyPromise = undefined

        if (
            playerContainerElement !== undefined &&
            playerContainerElement.parentNode !== null
        ) {
            playerContainerElement.parentNode.removeChild(
                playerContainerElement
            )
            playerContainerElement = undefined
        }

        isWatchingVideo.value = false
        if (typeof document !== 'undefined') {
            document.body.classList.remove('overflow-hidden')
        }
    }

    function showVideo() {
        if (playerContainerElement === undefined) {
            return
        }

        playerContainerElement.style.display = 'flex'
        isWatchingVideo.value = true

        if (typeof document !== 'undefined') {
            document.body.classList.add('overflow-hidden')
        }

        schedulePlaybackStallTimeout()
    }

    async function playVideo() {
        if (videoPlayer === undefined) {
            throw new Error('Video player was not created')
        }

        const playPromise = videoPlayer.play()
        if (playPromise !== undefined) {
            await playPromise
        }
    }

    function createPlayerReadyPromise(player: Player): Promise<void> {
        return new Promise((resolve, reject) => {
            player.ready(() => {
                const controlBar = player.getChild('controlBar')
                const progressControl =
                    controlBar !== undefined && 'getChild' in controlBar
                        ? controlBar.getChild('progressControl')
                        : undefined

                if (
                    progressControl !== undefined &&
                    'disable' in progressControl &&
                    typeof progressControl.disable === 'function' &&
                    !import.meta.env.DEV
                ) {
                    progressControl.disable()
                }

                resolve()
            })

            player.one('error', () => {
                reject(new Error('Video player failed to initialize'))
            })
        })
    }

    function withTimeout<T>(
        promise: Promise<T>,
        timeoutMs: number
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
                reject(new Error('Timed out waiting for video player'))
            }, timeoutMs)

            promise.then(
                result => {
                    clearTimeout(timeoutId)
                    resolve(result)
                },
                error => {
                    clearTimeout(timeoutId)
                    reject(error)
                }
            )
        })
    }

    async function setupVideoPlayer() {
        if (
            videoUrl === null ||
            videoPlayer !== undefined ||
            typeof document === 'undefined'
        ) {
            return
        }

        const videojs = await ensureVideoJsLoaded()

        const container = document.createElement('div')
        container.style.position = 'fixed'
        container.style.inset = '0'
        container.style.background = 'rgba(0, 0, 0, 0.85)'
        container.style.display = 'none'
        container.style.alignItems = 'center'
        container.style.justifyContent = 'center'
        container.style.zIndex = '10000'

        const videoElement = document.createElement('video')
        videoElement.className = 'video-js vjs-default-skin'
        videoElement.setAttribute('playsinline', 'true')
        videoElement.setAttribute('controlslist', 'noremoteplayback nodownload')
        videoElement.disablePictureInPicture = true

        container.appendChild(videoElement)
        document.body.appendChild(container)
        playerContainerElement = container

        const mimeType = inferVideoMimeType(videoUrl)
        const player = videojs(videoElement, {
            controls: true,
            preload: 'auto',
            autoplay: false,
            responsive: true,
            fluid: false,
            controlBar: {
                pictureInPictureToggle: false
            },
            sources:
                mimeType !== undefined
                    ? [{ src: videoUrl, type: mimeType }]
                    : [{ src: videoUrl }]
        })

        playerReadyPromise = createPlayerReadyPromise(player)
        updatePlayerDimensions(player)

        player.on('loadedmetadata', () => {
            const duration = player.duration()
            if (
                typeof duration === 'number' &&
                !Number.isNaN(duration) &&
                Number.isFinite(duration)
            ) {
                watchThresholdSeconds = Number(
                    Math.max(5, Math.floor(duration) - 20)
                )
            }

            updatePlayerDimensions(player)
        })

        player.on('playing', () => {
            clearPlaybackStallTimeout()
        })

        player.on('waiting', () => {
            if (isWatchingVideo.value) {
                schedulePlaybackStallTimeout()
            }
        })

        player.on('stalled', () => {
            if (isWatchingVideo.value) {
                schedulePlaybackStallTimeout()
            }
        })

        player.on('timeupdate', () => {
            const currentTime = player.currentTime()
            if (
                typeof currentTime === 'number' &&
                !alreadyWatchedVideo &&
                currentTime >= watchThresholdSeconds
            ) {
                alreadyWatchedVideo = true
            }

            if (isWatchingVideo.value) {
                clearPlaybackStallTimeout()
            }
        })

        player.on('ended', () => {
            hideVideo()
        })

        player.on('error', () => {
            if (isLoadingVideo.value) {
                return
            }

            hideVideo()
            resetPlayer()
            showAlert(
                'Whoops!',
                'It appears the video encountered an error while loading. Please reload and try again.'
            )
        })

        videoPlayer = player
    }

    onMounted(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleWindowResize)
        }
        void setupVideoPlayer()
    })

    onUnmounted(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', handleWindowResize)
        }
        resetPlayer()
    })

    async function handleWatchVideo() {
        if (alreadyWatchedVideo) {
            handleContinue()
            return
        }

        if (!validateContinue()) {
            return
        }

        if (videoUrl === null) {
            handleContinue()
            return
        }

        isLoadingVideo.value = true
        try {
            await setupVideoPlayer()

            if (playerReadyPromise === undefined) {
                throw new Error('Video player was not created')
            }

            await withTimeout(playerReadyPromise, 10000)
            if (videoPlayer === undefined) {
                throw new Error('Video player was not created')
            }

            updatePlayerDimensions(videoPlayer)
            showVideo()
            await withTimeout(playVideo(), 10000)
        } catch (_error) {
            console.error(_error)
            hideVideo()
            resetPlayer()
            showAlert(
                'Whoops!',
                'It appears the video is having trouble loading. Please reload and try again.'
            )
            return
        } finally {
            isLoadingVideo.value = false
        }
    }

    function handleAlreadyWatched() {
        alreadyWatchedVideo = true
        handleWatchedAndContinue()
        hideVideo()
    }

    const showAlreadyWatchButton = computed(
        () => isWatchingVideo.value && allowSkippingVideo
    )

    return {
        isLoadingVideo,
        isWatchingVideo,
        showAlreadyWatchButton,
        handleWatchVideo,
        handleAlreadyWatched
    }
}
